/**
 * Which basemap a MapLibre map draws: light (OpenFreeMap positron) or dark
 * (our edited copy of OpenFreeMap dark), both vendored in `./basemaps` (#342).
 *
 * The user picks a Map theme in the map's own settings: Auto follows the
 * app's theme, Light and Dark override it. Pure and dependency-free, so both
 * apps resolve it the same way.
 */

export type BasemapTheme = 'light' | 'dark';

export type BasemapThemePreference = 'auto' | BasemapTheme;

/** Every preference, in the order the Map theme control lists them. */
export const BASEMAP_THEME_PREFERENCES: readonly BasemapThemePreference[] = [
  'auto',
  'light',
  'dark',
];

export const isBasemapThemePreference = (
  value: unknown,
): value is BasemapThemePreference =>
  (BASEMAP_THEME_PREFERENCES as readonly unknown[]).includes(value);

/**
 * The basemap to draw. A missing or unknown preference (none stored yet, or
 * one written by a newer build) means Auto: follow the app.
 */
export const resolveBasemapTheme = (
  pref: BasemapThemePreference | null | undefined,
  appIsDark: boolean,
): BasemapTheme => {
  if (pref === 'light' || pref === 'dark') {
    return pref;
  }
  return appIsDark ? 'dark' : 'light';
};

/**
 * Each basemap's land (its `background`) and water fill, for whatever is
 * drawn or measured against the map outside MapLibre: a panel edge, a
 * contrast test. Depictions, tested equal to each document's paint.
 */
export const BASEMAP_LAND_COLOR: Readonly<Record<BasemapTheme, string>> = {
  light: '#f2f3f0',
  dark: '#121417',
};

export const BASEMAP_WATER_COLOR: Readonly<Record<BasemapTheme, string>> = {
  light: '#c2c8ca',
  dark: '#2f3843',
};
